<template>
	<div class="pageRoot" :style="pageRootStyle">
		<div class="pageHeading">Service Settings</div>

		<div v-if="(error)" class="errorBox">{{error}}</div>
		<div v-else-if="loading" class="card loadingCard"><ScaleLoader /> <span>Loading…</span></div>
		<template v-else-if="editObj && editSpec">
			<div class="card toolbarCard">
				<button type="button" class="gradientButton lightBlue wideButton" @click="openAwsPanel">Look Up AWS Resources</button>
				<span class="hintText">Lists the Target Group ARNs and EC2 instance IDs available in the configured AWS account, for copying into the fields below.</span>
			</div>

			<div class="card settingsCard">
				<Editor :object="editObj" :spec="editSpec" :externalHelpToggle="true" :helpVisible="showEditorHelp" @valueChanged="valueChanged" />
			</div>

			<div class="card actionsCard">
				<div class="actionGroup">
					<button type="button" class="gradientButton green wideButton" @click="SaveChanges">Save Changes</button>
					<button type="button" class="gradientButton lightBlue wideButton" @click="ForgetChanges">Forget Changes / Reload Form</button>
				</div>
				<div class="actionGroup">
					<button type="button" class="gradientButton tdsHealthBrown wideButton" @click="RestartServerClicked">Restart Server</button>
					<span class="hintText">Some settings only take effect after a restart.  Active web interface sessions will be lost.</span>
				</div>
			</div>
		</template>

		<!-- Non-modal panels pinned below the top nav at the right edge of the viewport, so they remain available no
			 matter how far the settings form has been scrolled.  Stacking them in one fixed container keeps the AWS
			 resource list clear of the help toggle without hard-coding either panel's height. -->
		<div class="floatingStack" :style="floatingStackStyle">
			<div class="helpTogglePanel" v-if="!error && !loading && editObj && editSpec">
				<label class="helpToggleLabel" for="showEditorHelpCheckbox">
					<input type="checkbox" id="showEditorHelpCheckbox" v-model="showEditorHelp" /> <span>Show Editor Help</span>
				</label>
			</div>
			<div v-if="awsPanelVisible" class="awsResourcePanel" role="dialog" aria-label="AWS Resources">
				<div class="awsPanelHeader">
					<span class="awsPanelTitle">AWS Resources</span>
					<button type="button" class="awsPanelBtn" @click="loadAwsResources" :disabled="awsPanelLoading">Refresh</button>
					<button type="button" class="awsPanelBtn awsPanelClose" @click="awsPanelVisible = false" title="Close">✕</button>
				</div>
				<div v-if="awsPanelLoading" class="awsPanelLoading"><ScaleLoader /> Querying AWS…</div>
				<div v-else-if="awsPanelError" class="errorBox">{{awsPanelError}}</div>
				<template v-else>
					<div class="awsPanelSection">Target Groups</div>
					<p v-if="!awsTargetGroups.length" class="awsPanelEmpty">No target groups were found.</p>
					<div v-for="tg in awsTargetGroups" :key="tg.arn" class="awsResourceRow">
						<div class="awsResourceName">{{tg.name}} <span class="awsResourceMeta">{{tg.protocol}}:{{tg.port}}</span></div>
						<div class="awsResourceValueRow">
							<code class="awsResourceValue">{{tg.arn}}</code>
							<button type="button" class="awsCopyBtn" @click="copyResourceValue(tg.arn)" :title="'Copy the ARN of \'' + tg.name + '\' to the clipboard'">Copy</button>
						</div>
					</div>
					<div class="awsPanelSection">EC2 Instances</div>
					<p v-if="!awsInstances.length" class="awsPanelEmpty">No instances were found.</p>
					<div v-for="inst in awsInstances" :key="inst.instanceId" class="awsResourceRow">
						<div class="awsResourceName">{{inst.name || '(unnamed)'}} <span class="awsResourceMeta">{{inst.state}}</span></div>
						<div class="awsResourceValueRow">
							<code class="awsResourceValue">{{inst.instanceId}}</code>
							<button type="button" class="awsCopyBtn" @click="copyResourceValue(inst.instanceId)" :title="'Copy the instance ID of \'' + (inst.name || inst.instanceId) + '\' to the clipboard'">Copy</button>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
	import Editor from 'appRoot/vues/common/editor/Editor.vue';
	import { GetSettingsData, SetSettingsData, RestartServer } from 'appRoot/api/SettingsData';
	import { GetAwsTargetGroupResources } from 'appRoot/api/MonitoringConfig';
	import { ModalConfirmDialog } from 'appRoot/scripts/ModalDialog';
	import { CopyToClipboard } from 'appRoot/scripts/Util';
	import EventBus from 'appRoot/scripts/EventBus';

	export default {
		components: { Editor },
		data()
		{
			return {
				error: null,
				loading: false,
				editObj: null,
				editSpec: null,
				showEditorHelp: false,
				awsPanelVisible: false,
				awsPanelLoading: false,
				awsPanelError: null,
				awsTargetGroups: [],
				awsInstances: []
			};
		},
		computed:
		{
			/**
			 * The page paints its own background, so it must reach the footer even when the form is short.
			 */
			pageRootStyle()
			{
				return {
					minHeight: "calc(100vh - " + (EventBus.topNavHeight + EventBus.footerHeight) + "px)"
				};
			},
			/**
			 * Keeps the fixed-position panel stack clear of the top nav (whose height changes when it wraps) while the
			 * nav is on screen, then lets it rise to the top edge once the nav has scrolled out of view.
			 */
			floatingStackStyle()
			{
				let top = Math.max(10, Math.max(0, EventBus.topNavHeight) - EventBus.pageYOffset + 10);
				return {
					top: top + "px",
					maxHeight: "calc(100vh - " + (top + 10) + "px)"
				};
			}
		},
		methods:
		{
			loadSettings()
			{
				this.loading = true;
				this.editObj = null;
				this.editSpec = null;
				GetSettingsData()
					.then(data =>
					{
						if (data.success)
						{
							this.editObj = data.settings;
							this.editSpec = data.editSpec;

							if (data.settings.loginStyle)
								appContext.loginStyle = data.settings.loginStyle;
						}
						else
							this.error = data.error;
					})
					.catch(err =>
					{
						this.error = err.message;
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			valueChanged(key, value)
			{
				this.editObj[key] = value;
			},
			SaveChanges()
			{
				this.loading = true;
				this.editObj.appPath = '/' + this.editObj.appPath.replace(/^\/+|\/+$/g, '') + '/';
				if (this.editObj.appPath === '//')
					this.editObj.appPath = '/';
				SetSettingsData(this.editObj)
					.then(data =>
					{
						if (data.success)
						{
							if (data.message)
								toaster.success(data.message);
							appContext.appPath = this.editObj.appPath;
							this.loadSettings();
						}
						else
						{
							toaster.error(data.error);
							this.loading = false;
						}
					})
					.catch(err =>
					{
						toaster.error(err.message);
						this.loading = false;
					});
			},
			ForgetChanges()
			{
				this.loadSettings();
			},
			openAwsPanel()
			{
				this.awsPanelVisible = true;
				this.loadAwsResources();
			},
			loadAwsResources()
			{
				this.awsPanelLoading = true;
				this.awsPanelError = null;
				GetAwsTargetGroupResources()
					.then(data =>
					{
						if (data.success)
						{
							this.awsTargetGroups = data.targetGroups || [];
							this.awsInstances = data.instances || [];
						}
						else
							this.awsPanelError = data.error;
					})
					.catch(err =>
					{
						this.awsPanelError = err.message || String(err);
					})
					.finally(() =>
					{
						this.awsPanelLoading = false;
					});
			},
			copyResourceValue(value)
			{
				CopyToClipboard(value);
				toaster.Success('Copied "' + value + '" to the clipboard.');
			},
			RestartServerClicked()
			{
				ModalConfirmDialog("Are you sure you want to restart the service? Active web interface sessions will be lost.", "Confirm")
					.then(userClickedYes =>
					{
						if (userClickedYes)
						{
							this.loading = true;
							RestartServer()
								.then(data =>
								{
									setTimeout(() =>
									{
										this.loadSettings();
									}, 4000);
								})
								.catch(err =>
								{
									this.loading = false;
									toaster.error(err);
								});
						}
					});
			}
		},
		created()
		{
			this.loadSettings();
		}
	}
</script>

<style scoped>
	.pageRoot
	{
		box-sizing: border-box;
		padding: 14px 14px 24px 14px;
		background-color: #EFF2F5;
		color: #24303A;
	}

	.pageHeading
	{
		font-size: 28px;
		font-weight: bold;
		color: var(--primary-color-text);
		padding-bottom: 8px;
		margin-bottom: 14px;
		border-bottom: 2px solid var(--primary-color-light3);
		max-width: 1500px;
	}

	.card
	{
		box-sizing: border-box;
		max-width: 1500px;
		background-color: #FFFFFF;
		border: 1px solid #DCE3E9;
		border-radius: 8px;
		box-shadow: 0px 1px 2px rgba(16,32,48,0.06), 0px 2px 8px rgba(16,32,48,0.06);
		padding: 12px 14px;
		margin-bottom: 16px;
	}

	.errorBox
	{
		box-sizing: border-box;
		max-width: 1500px;
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		border-radius: 6px;
		color: #660000;
		font-weight: bold;
		padding: 10px 14px;
		margin-bottom: 16px;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.loadingCard
	{
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		padding: 40px;
	}

	/* ---- Cards ------------------------------------------------------------------------------------------------- */
	.toolbarCard,
	.actionsCard
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 10px 18px;
	}

	.actionsCard
	{
		background: linear-gradient(to bottom, #FFFFFF, #F5F8FA);
	}

	.actionGroup
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px 14px;
	}

		.actionGroup + .actionGroup
		{
			margin-left: auto;
		}

	.settingsCard
	{
		/* The settings values are long strings (paths, URLs, ARNs), so the field column is much wider here than the
		   default used by the compact editors that live inside modal dialogs. */
		--propFieldWidth: 640px;
		padding: 6px 4px;
	}

	.hintText
	{
		flex: 1 1 260px;
		min-width: 0;
		color: #5B6976;
		font-size: 0.9em;
	}

	/* Globals.scss caps .gradientButton at 140px, which clips the multi-word labels used on this page. */
	.wideButton
	{
		max-width: none;
		font-size: 15px;
		margin-right: 0px;
	}

	/* ---- Floating panel stack ---------------------------------------------------------------------------------- */
	.floatingStack
	{
		position: fixed;
		right: 12px;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 8px;
		max-width: calc(100vw - 24px);
		z-index: 100;
		pointer-events: none; /* so the container's empty area does not swallow clicks on the form underneath */
	}

		.floatingStack > *
		{
			pointer-events: auto;
		}

	.helpTogglePanel
	{
		flex: 0 0 auto;
		background: linear-gradient(to bottom, #FFFFFF, #F2F6F9);
		border: 1px solid #B7C9D6;
		border-left: 4px solid var(--primary-color);
		border-radius: 6px;
		box-shadow: 0px 2px 10px rgba(16,32,48,0.25);
		padding: 8px 12px;
	}

	.helpToggleLabel
	{
		display: flex;
		align-items: center;
		gap: 8px;
		cursor: pointer;
		user-select: none;
		font-size: 15px;
		font-weight: bold;
		color: var(--primary-color-text);
		white-space: nowrap;
	}

		.helpToggleLabel input
		{
			width: 16px;
			height: 16px;
			margin: 0px;
			accent-color: var(--primary-color);
			cursor: pointer;
		}

	/* ---- AWS resource panel -------------------------------------------------------------------------------------- */
	.awsResourcePanel
	{
		flex: 0 1 auto;
		min-height: 0;
		width: 470px;
		max-width: 100%;
		overflow-y: auto;
		background-color: #FFFFFF;
		border: 1px solid #C3CDD6;
		border-radius: 8px;
		box-shadow: 0px 4px 24px rgba(16,32,48,0.35);
		padding: 12px;
		box-sizing: border-box;
	}

	.awsPanelHeader
	{
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 8px;
	}

	.awsPanelTitle
	{
		font-size: 1.2em;
		font-weight: bold;
		color: var(--primary-color-text);
		flex: 1 1 auto;
	}

	.awsPanelBtn
	{
		flex: 0 0 auto;
		background-color: #F6F8FA;
		border: 1px solid #BFCBD4;
		border-radius: 4px;
		padding: 3px 10px;
		cursor: pointer;
		font-family: inherit;
		font-size: 13px;
		color: #24303A;
	}

		.awsPanelBtn:hover:enabled
		{
			background-color: #FFFFFF;
			border-color: var(--primary-color);
		}

		.awsPanelBtn:disabled
		{
			opacity: 0.55;
			cursor: not-allowed;
		}

	.awsPanelClose
	{
		padding: 3px 8px;
	}

	.awsPanelLoading
	{
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px 0px;
	}

	.awsPanelSection
	{
		font-weight: bold;
		color: var(--primary-color-text);
		text-transform: uppercase;
		font-size: 0.8em;
		letter-spacing: 0.05em;
		border-bottom: 2px solid #DDE4EA;
		padding-bottom: 3px;
		margin: 14px 0px 6px 0px;
	}

		.awsPanelSection:first-of-type
		{
			margin-top: 4px;
		}

	.awsPanelEmpty
	{
		color: #7A8894;
		font-style: italic;
		margin: 6px 0px;
	}

	.awsResourceRow
	{
		padding: 6px 0px;
		border-bottom: 1px solid #EEF1F4;
	}

		.awsResourceRow:last-child
		{
			border-bottom: none;
		}

	.awsResourceName
	{
		font-weight: bold;
	}

	.awsResourceMeta
	{
		font-weight: normal;
		color: #8A96A1;
		font-size: 0.85em;
		margin-left: 4px;
	}

	.awsResourceValueRow
	{
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin-top: 2px;
	}

	.awsResourceValue
	{
		min-width: 0;
		font-family: Consolas, monospace;
		font-size: 0.85em;
		background-color: #F1F5F8;
		border: 1px solid #E1E8EE;
		border-radius: 3px;
		padding: 2px 6px;
		word-break: break-all;
	}

	.awsCopyBtn
	{
		flex: 0 0 auto;
		font-size: 0.8em;
		padding: 2px 8px;
		border: 1px solid #BFCBD4;
		border-radius: 3px;
		background-color: #FAFBFC;
		font-family: inherit;
		color: #24303A;
		cursor: pointer;
	}

		.awsCopyBtn:hover
		{
			background-color: #FFFFFF;
			border-color: var(--primary-color);
		}

	/* On a narrow screen the pinned help toggle takes most of the width, so the heading has to give way. */
	@media (max-width: 600px)
	{
		.pageHeading
		{
			font-size: 22px;
		}
	}
</style>
